<script setup lang="ts">
import {computed, ref} from "vue";
import { useI18n } from "vue-i18n";
import {SetWidget} from "@/assets/ts/stepWidget";
import {useStore} from "vuex";
import {i18n} from "@/main";
import { Diamond } from '@/store/modules/diamonds'
import {Certificate} from "@/assets/ts/certificate";


const props = defineProps<{
  diamond: Diamond
}>()

const { t } = useI18n();

const getFormName = (form: string) => {
  const key = 'components.diamonds.details.form.' + form;
  const translation = t(key);
  return translation !== key ? translation : form;
}

const getPolishName = (polish: string) => {
  const key = 'components.diamonds.details.polish.' + polish;
  const translation = t(key);
  return translation !== key ? translation : polish;
}

const getFluorescenceName = (fluo: string) => {
  const key = 'components.diamonds.details.fluorescence.' + fluo;
  const translation = t(key);
  return translation !== key ? translation : fluo;
}

/**
 *
 */

const createOrderData = (orderType) => {
  let itemNumber = (
      props.diamond.institute + props.diamond.certificate
  ).toUpperCase();

  if (typeof props.diamond.link != "undefined") {
    itemNumber = props.diamond.link.toUpperCase();
  }

  return btoa(
      JSON.stringify({
        fast_order: false,
        order_type: orderType,
        language: "de",
        rings: [
          {
            name: itemNumber,
            size: 0,
            gravure: "",
            gravure_type: 0,
            gravure_outside: "",
            engravingOutsideType: 0,
            item_data: {
              master: [itemNumber],
            },
            master_set: itemNumber,
            itemnumber: itemNumber,
          },
        ],
      })
  );
};

const orderDataDiamond = computed(() => {
  return createOrderData("diamond");
});

const orderDataJewelry = computed(() => {
  return createOrderData("jewelry");
});

const goBack = () => {
  const lastSearch = localStorage.getItem('lastDiamondSearch')
  if (lastSearch) {
    window.location.href = lastSearch
  } else {
    // The main search is the current url without the certificates id
    let path = window.location.pathname
    if (path.endsWith('/')) {
      path = path.slice(0, -1)
    }
    const parts = path.split('/')
    parts.pop()
    window.location.href = parts.join('/') + '/'
  }
}

/**
 * add diamond to conf session
 */

const store = useStore();
const setWidgetApi = new SetWidget();

const isConfSession = ref(setWidgetApi.getConfSession() != null)

const addToConfSession = () => {
  const confSession = setWidgetApi.getConfSession();
  if (!confSession) return;

  store.commit("loading/actionStarted");

  const locale = (i18n.global.locale as any).value || "de-DE";

  let [language, country] = locale.split("-");

  if (locale.split("-").length === 3) {
    language = locale.split("-").splice(0, 2).join("-");
    country = locale.split("-").splice(2, 1).join("-");
  }

  fetch(
      process.env.VUE_APP_API_HOST +
      "/session/" +
      country.toLowerCase() +
      "/" +
      language.toLowerCase() +
      "/" +
      confSession.toLowerCase(),
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          item: 0,
          action: "diamond",
          value: props.diamond.link
        }),
      }
  )
      .then((response) => {
        if (response.status === 200) {
          document.location.href = setWidgetApi.createPath("conf");
        }
      })
      .finally(() => {
        store.commit("loading/actionFinished");
      });
};

const openCatalog = () => {
  document.location.href = setWidgetApi.createPath("catalog", props.diamond.link);
}

</script>

<template>
  <div class="diamond-info">
    <div class="flex items-center gap-4 mb-6">
      <span class="text-2xl font-serif text-gray-900">{{ diamond.price.toLocaleString('de-DE') }} {{ $t('common.currencySymbol') }}</span>
      <span class="text-xs text-gray-400">{{ $t('components.itemV2.itemV2Main.incl_vat') }}</span>
      <div class="flex gap-2 ml-auto text-gray-400">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 cursor-pointer hover:text-gray-600">
          <path stroke-linecap="round" stroke-linejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0-10.628a2.25 2.25 0 114.5 0 2.25 2.25 0 01-4.5 0zm0 10.628a2.25 2.25 0 114.5 0 2.25 2.25 0 01-4.5 0z" />
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 cursor-pointer hover:text-gray-600">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
        </svg>
      </div>
    </div>

    <!-- Selection Look & Feel -->
    <div class="space-y-4">
      <div class="border-t border-gray-100 pt-4">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.type') }}:</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">{{ $t('components.diamonds.details.type.' + diamond.type) }}</span>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100 pt-2">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.form') }}:</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">{{ getFormName(diamond.form) }}</span>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100 pt-2">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.size') }}:</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">{{ diamond.size.toLocaleString('de-DE') }} {{ $t('components.diamonds.details.carat.unit') }}</span>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100 pt-2">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.color') }}:</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">{{ diamond.color }}
              {{ diamond.fancy_intensity && diamond.colored ? `(${diamond.fancy_intensity})` : '' }}
            </span>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100 pt-2">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.clarity') }}:</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">{{ diamond.clarity }}</span>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100 pt-2">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.polishSymmetry') }}:</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">{{ getPolishName(diamond.polish) }} / {{ getPolishName(diamond.symmetry) }}</span>
          </div>
        </div>
      </div>

       <div class="border-t border-gray-100 pt-2">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.fluorescence') }}:</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">{{ getFluorescenceName(diamond.fluorescence) }}</span>
          </div>
        </div>
      </div>

       <div class="border-t border-gray-100 pt-2">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.dimension') }}:</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-600">{{ diamond.width.toLocaleString('de-DE') }} x {{ diamond.length.toLocaleString('de-DE') }} x {{ diamond.height.toLocaleString('de-DE') }} {{ $t('components.diamonds.details.fullSpecs.unit_mm') }}</span>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100 pt-2 pb-8">
        <div class="flex justify-between items-center group cursor-pointer py-2">
          <span class="text-xs font-bold uppercase tracking-wider">{{ $t('components.diamonds.details.fullSpecs.institute') }}:</span>
          <div class="flex items-center gap-2 justify-center text-gray-600">
            <span class="text-sm ">{{ diamond.institute }}</span>
            <a :href="Certificate.getUrl(diamond.institute, diamond.certificate)" target="_blank">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="space-y-3 mt-8">
      <button
          v-if="isConfSession"
          @click="addToConfSession()"
          class="block w-full text-white bg-brand-main p-2 text-center hover:bg-white hover:text-brand-main border border-brand-main text-sm font-light"
      >
        {{ $t("components.diamonds.details.DiamondDetailsNav.addToItem") }}
      </button>
      <button
          v-else
          @click="openCatalog()"
          class="block w-full text-white bg-brand-main p-2 text-center hover:bg-white hover:text-brand-main border border-brand-main text-sm font-light"
      >
        {{ $t("components.diamonds.details.DiamondDetailsNav.addToItem") }}
      </button>
      <form
          v-if="!isConfSession"
          method="post"
          action="https://www.rubin-ec84.de:7004/store/faces/login.xhtml"
          target="_blank"
      >
        <button
            type="submit"
            class="block w-full text-white bg-brand-main p-2 text-center hover:bg-white hover:text-brand-main border border-brand-main text-sm font-light"        >
          {{ $t("components.diamonds.details.head.button.buyJewelry") }}
        </button>
        <input type="hidden" name="lang" value="de" />
        <input type="hidden" name="action" value="order" />
        <input type="hidden" name="order" :value="orderDataJewelry" />
      </form>
      <button
          @click="goBack"
          class="block w-full text-white bg-brand-main p-2 text-center hover:bg-white hover:text-brand-main border border-brand-main text-sm font-light"      >
        {{ $t("components.diamonds.details.DiamondDetailsNav.backToSearch") }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.font-serif {
  font-family: "Ivy Mode", serif;
}
</style>
